import { useCallback, useEffect, useState } from 'react';

import { facilityService } from '@/services';
import type { Facility, FacilityFilters } from '@/types';

interface UseFacilitiesResult {
  readonly facilities: readonly Facility[];
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
  readonly loading: boolean;
  readonly error: string | null;
  readonly refetch: () => Promise<void>;
  readonly setPage: (page: number) => void;
  readonly setFilters: (filters: FacilityFilters) => void;
}

export function useFacilities(
  initialFilters?: FacilityFilters,
  initialPageSize = 50
): UseFacilitiesResult {
  const [facilities, setFacilities] = useState<readonly Facility[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(initialPageSize);
  const [filters, setFilters] = useState<FacilityFilters>(initialFilters || {});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await facilityService.fetchFacilities(
        filters,
        page,
        pageSize
      );
      setFacilities(response.facilities);
      setTotal(response.total);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to fetch facilities';
      setError(message);
      console.error('Error fetching facilities:', err);
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const handleSetFilters = useCallback((newFilters: FacilityFilters) => {
    setFilters(newFilters);
    setPage(1); // Reset to first page when filters change
  }, []);

  return {
    facilities,
    total,
    page,
    pageSize,
    loading,
    error,
    refetch: fetchData,
    setPage,
    setFilters: handleSetFilters,
  };
}
